import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { WrappedControlProps } from 'app/components/Controls/WrappedControlProps';
import { UtilService } from 'app/services/util.service';
import { isValidWord } from 'app/validators/validateWord';
import { getEveryWordCapitalized } from 'app/shared/helpers/getEveryWordCapitalized';
import { isAutofillChange } from 'app/shared/helpers/isAutofillChange';
import { selectShouldCheckIfConsentResetRequired } from 'app/store/data/consents';
import { getQuotationId, selectQuotation } from 'app/store/data/quotation';
import { FieldsEnum, FormsEnum } from 'app/shared/enums';
import {
  checkAndResetMarketingConsentIfRequired,
  getConsentResetPersonFlag,
  getConsentResetPersonGroup,
  shouldCheckConsentResetForTrackedPerson,
} from 'app/shared/helpers/checkAndResetMarketingConsent';

const containsValidCharacters = (value: string) =>
  /^$|^[a-zA-ZąĄćĆęĘłŁńŃóÓśŚżŻźŹ' -]+(|[*]{3}|[*]{2}|[*])$/.test(value);

const clearWhitespacesAndAnonymization = (value: string) =>
  value.replace(/\*| +(?= )/g, '');

const containsDuplicateDash = (value: string) => /(-+)\1/.test(value);

export function useLastNameControl({
  input,
  meta: { touched, valid },
  onValueChange,
  onControlBlur,
  onClick,
  questionKey,
  formName = FormsEnum.CalculationForm,
}: WrappedControlProps) {
  const dispatch = useDispatch();
  const shouldCheckIfConsentResetRequired = useSelector(
    selectShouldCheckIfConsentResetRequired
  );
  const quotationId = useSelector(getQuotationId);
  const { insurantSelection } = useSelector(selectQuotation);
  const [focused, setFocused] = useState(false);
  const error = touched && !valid;
  const isValueCheckVisible =
    !focused && UtilService.hasValue(input.value) && !error;

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    handleFocus(e.target.value);
    setFocused(true);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (
      (!containsValidCharacters(value) && value.trim() !== '') ||
      value.length > 50 ||
      containsDuplicateDash(value)
    ) {
      return;
    }

    input.onChange(value);

    if (onValueChange) {
      onValueChange(value, false, false);
    }

    if (isAutofillChange(event.target)) {
      handleBlur(value);
    }
  };

  const handleFocus = (value: string) => {
    const clearedValue = clearWhitespacesAndAnonymization(value);

    if (clearedValue !== value) {
      input.onChange(clearedValue);
      if (onValueChange) {
        onValueChange(clearedValue, false, false);
      }
    }
  };

  const handleBlur = async (autofilledValue?: string) => {
    const isAutofilled = autofilledValue !== undefined;
    const currentValue =
      autofilledValue === undefined ? input.value : autofilledValue;
    const formattedValue = getEveryWordCapitalized(
      currentValue.replace(/^-+|-+$/g, '').trim()
    );
    if (formattedValue !== currentValue) {
      input.onChange(formattedValue);
      if (onValueChange) {
        onValueChange(formattedValue, false, false);
      }
    }

    setFocused(false);

    input.onBlur(formattedValue);

    const shouldPatch = isValidWord(formattedValue);

    const isCheckoutFirstCoownerLastNameControl =
      questionKey === FieldsEnum.CheckoutCoowners1LastName;
    const isCheckoutSecondCoownerLastNameControl =
      questionKey === FieldsEnum.CheckoutCoowners2LastName;

    const trackedPerson =
      questionKey === FieldsEnum.OwnerLastName ||
      questionKey === FieldsEnum.CheckoutOwnerLastName
        ? 'owner'
        : questionKey === FieldsEnum.Coowners1LastName ||
          isCheckoutFirstCoownerLastNameControl
        ? 'coowner1'
        : questionKey === FieldsEnum.Coowners2LastName ||
          isCheckoutSecondCoownerLastNameControl
        ? 'coowner2'
        : questionKey === FieldsEnum.CheckoutInsurantLastName
        ? 'different'
        : null;

    const shouldTrackThisPerson =
      trackedPerson &&
      shouldCheckConsentResetForTrackedPerson(trackedPerson, insurantSelection);
    const personGroup = getConsentResetPersonGroup(insurantSelection);
    const shouldCheck = getConsentResetPersonFlag(
      shouldCheckIfConsentResetRequired,
      personGroup,
      'lastName'
    );

    if (shouldPatch && formattedValue && shouldTrackThisPerson && shouldCheck) {
      await checkAndResetMarketingConsentIfRequired({
        quotationId,
        dispatch,
        shouldCheck,
        requestData: {
          [personGroup]: {
            lastName: formattedValue,
          },
        },
        fieldToResetPath: {
          type: 'person',
          personGroup,
          field: 'lastName',
        },
        formName,
        consentFieldKey:
          formName === FormsEnum.CheckoutForm
            ? FieldsEnum.InsuranceMarketingConsent
            : FieldsEnum.InsuranceMarketingEmailConsent,
      });
    }

    if (onControlBlur) {
      setTimeout(() => {
        onControlBlur(
          shouldPatch,
          true,
          false,
          isAutofilled,
          isAutofilled ? formattedValue : undefined
        );
      }, 0);
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return {
    error,
    focused,
    handleChange,
    handleBlur,
    handleClick,
    isValueCheckVisible,
    onFocus,
  };
}
